import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Heart, User, X, Loader2, Users, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Générer ou récupérer un visitor ID persistant
const getVisitorId = () => {
  let visitorId = localStorage.getItem('sada_visitor_id');
  if (!visitorId) {
    visitorId = 'visitor_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('sada_visitor_id', visitorId);
  }
  return visitorId;
};

// Récupérer le nom sauvegardé du visiteur
const getSavedLikerName = () => {
  return localStorage.getItem('sada_liker_name') || '';
};

const saveLikerName = (name) => {
  localStorage.setItem('sada_liker_name', name);
};

const LikeButton = ({ 
  targetType, 
  targetId, 
  size = 'default', // 'small', 'default', 'large'
  showCount = true,
  showNames = true,
  language = 'fr'
}) => {
  const [likesCount, setLikesCount] = useState(0);
  const [likers, setLikers] = useState([]);
  const [hasLiked, setHasLiked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [likerName, setLikerName] = useState(getSavedLikerName());
  const visitorId = getVisitorId();

  const fetchLikes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/likes/${targetType}/${targetId}?visitor_id=${visitorId}&limit=10`
      );
      setLikesCount(response.data.total_count);
      setLikers(response.data.likes || []);
      setHasLiked(response.data.has_liked);
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  }, [targetType, targetId, visitorId]);

  useEffect(() => {
    fetchLikes();
  }, [fetchLikes]);

  const handleLikeClick = async () => {
    // Si pas encore de nom et premier like, demander le nom
    if (!hasLiked && !getSavedLikerName()) {
      setShowNameInput(true);
      return;
    }
    
    await submitLike(getSavedLikerName());
  };

  const submitLike = async (name) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/likes/`, {
        target_type: targetType,
        target_id: targetId,
        liker_name: name || 'Anonyme',
        visitor_id: visitorId
      });
      
      if (name && name !== 'Anonyme') {
        saveLikerName(name);
      }
      
      setLikesCount(response.data.likes_count);
      setHasLiked(response.data.action === 'added');
      setShowNameInput(false);
      
      // Refresh la liste des likers
      await fetchLikes();
      
      toast.success(
        response.data.action === 'added' 
          ? (language === 'fr' ? 'Merci pour votre like !' : 'Thanks for your like!')
          : (language === 'fr' ? 'Like retiré' : 'Like removed')
      );
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = () => {
    submitLike(likerName || 'Anonyme');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short'
    });
  };

  // Taille du bouton
  const sizeClasses = {
    small: 'h-7 px-2 text-xs',
    default: 'h-9 px-3 text-sm',
    large: 'h-11 px-4 text-base'
  };

  const iconSize = {
    small: 'w-3.5 h-3.5',
    default: 'w-4 h-4',
    large: 'w-5 h-5'
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Bouton Like */}
        <Button
          variant={hasLiked ? 'default' : 'outline'}
          size="sm"
          onClick={handleLikeClick}
          disabled={loading}
          className={`${sizeClasses[size]} ${hasLiked ? 'bg-red-500 hover:bg-red-600 text-white border-red-500' : 'border-gray-300 hover:border-red-300 hover:text-red-500'}`}
          data-testid={`like-btn-${targetId}`}
        >
          {loading ? (
            <Loader2 className={`${iconSize[size]} animate-spin`} />
          ) : (
            <Heart className={`${iconSize[size]} ${hasLiked ? 'fill-current' : ''}`} />
          )}
          {showCount && likesCount > 0 && (
            <span className="ml-1.5">{likesCount}</span>
          )}
        </Button>

        {/* Voir qui a liké */}
        {showNames && likesCount > 0 && (
          <button
            onClick={() => setShowModal(true)}
            className="text-xs text-gray-500 hover:text-blue-600 hover:underline flex items-center gap-1"
            data-testid={`see-likers-${targetId}`}
          >
            <Users className="w-3 h-3" />
            {language === 'fr' ? 'Voir les likes' : 'See who liked'}
          </button>
        )}
      </div>

      {/* Modal pour entrer le nom avant de liker */}
      <Dialog open={showNameInput} onOpenChange={setShowNameInput}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              {language === 'fr' ? 'Votre prénom' : 'Your name'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Entrez votre prénom pour que votre like soit visible (optionnel)'
                : 'Enter your name to show with your like (optional)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder={language === 'fr' ? 'Votre prénom' : 'Your first name'}
              value={likerName}
              onChange={(e) => setLikerName(e.target.value)}
              maxLength={50}
              autoFocus
              data-testid="liker-name-input"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => handleNameSubmit()}
                disabled={loading}
                className="flex-1 bg-red-500 hover:bg-red-600"
                data-testid="submit-like-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
                {language === 'fr' ? 'J\'aime' : 'Like'}
              </Button>
              <Button
                variant="outline"
                onClick={() => submitLike('Anonyme')}
                disabled={loading}
              >
                {language === 'fr' ? 'Anonyme' : 'Anonymous'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal liste des likers */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              {likesCount} {language === 'fr' ? 'personne(s) aime(nt) ceci' : 'people like this'}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {likers.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                {language === 'fr' ? 'Aucun like pour le moment' : 'No likes yet'}
              </p>
            ) : (
              <div className="space-y-2">
                {likers.map((liker, index) => (
                  <div 
                    key={liker.id || index}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                    data-testid={`liker-${index}`}
                  >
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                      {liker.liker_name === 'Anonyme' ? (
                        <User className="w-4 h-4" />
                      ) : (
                        <span className="font-semibold text-sm">
                          {liker.liker_name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900">
                        {liker.liker_name}
                      </p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(liker.created_at)}
                      </p>
                    </div>
                    <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                  </div>
                ))}
                {likesCount > likers.length && (
                  <p className="text-center text-sm text-gray-400 py-2">
                    +{likesCount - likers.length} {language === 'fr' ? 'autres' : 'more'}
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LikeButton;
